import { DEFAULT_DATE_FORMAT, formatAddress, PaymentMethod } from '@aslanpay/portal-common-lib';
import {
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';

import formatNumber from 'app/utils/formatting';
import { formatCurrency } from 'app/utils/strings';
import { format, parseISO } from 'date-fns';

import DetailsRow from '../UI/DetailsRow/DetailsRow';

import { OrderDetailsModalProps } from './types';

function paymentMethodToDisplay(paymentMethod: PaymentMethod) {
  switch (paymentMethod) {
    case 'INVOICE':
      return 'Invoice';
    case 'STRIPE':
      return 'Stripe';
    default:
      return 'N/A';
  }
}

function OrderDetailsModal({ isOpen, onClose, order, createdByName }: OrderDetailsModalProps) {
  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      size="2xl"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6" textStyle="h6SemiBold">
              Order details
            </Text>
          </HStack>
          <Text textStyle="smallRegular" color="gray.600" mt={2}>
            You are viewing the order details for a past points purchase.
          </Text>
        </ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing="20px">
            {/* Summary Section */}
            <Box bg="brand.mutedPurple" borderRadius="md" p="20px">
              <HStack spacing="60px" justify="space-between">
                <VStack align="flex-start" spacing="4px">
                  <Text textStyle="smallMedium" color="white">
                    Date
                  </Text>
                  <Text textStyle="h6SemiBold" color="white">
                    {format(parseISO(order.createdAt), DEFAULT_DATE_FORMAT)}
                  </Text>
                </VStack>
                <VStack align="flex-start" spacing="4px">
                  <Text textStyle="smallMedium" color="white">
                    Points purchased
                  </Text>
                  <Text textStyle="h6SemiBold" color="white">
                    {formatNumber(order.pointsAmount)}
                  </Text>
                </VStack>
                <VStack align="flex-start" spacing="4px">
                  <Text textStyle="smallMedium" color="white">
                    Cost
                  </Text>
                  <Text textStyle="h6SemiBold" color="white">
                    {formatCurrency(order.moneyAmount, 'GBP')}
                  </Text>
                </VStack>
              </HStack>
            </Box>

            {/* Details Section */}
            <VStack align="stretch" spacing="0px">
              <DetailsRow
                label="Payment Method:"
                value={paymentMethodToDisplay(order.paymentMethod)}
                showClipboard={false}
              />
              <DetailsRow label="Performed by:" value={createdByName} showClipboard={false} />
              <DetailsRow
                label="Legal Name:"
                value={order.companyName}
                showClipboard={false}
                valueTextStyle="smallSemiBold"
              />
              <DetailsRow
                label="Company Number:"
                value={order.companyNumber}
                showClipboard={false}
              />
              <DetailsRow label="Address:" value={formatAddress(order.companyAddress)} />
              <DetailsRow label="VAT Number:" value={order.vatNumber || 'N/A'} />
              <DetailsRow label="Invoice Recipient Email:" value={order.invoiceEmail || 'N/A'} />
              <DetailsRow label="Recipient Name:" value={order.invoiceRecipientName || 'N/A'} />
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} size="sm">
            Close details
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default OrderDetailsModal;
